import { useEffect, useState, type RefObject } from 'react';
import type { ComponentTemplate, Scene } from './schema/components';

/**
 * Synthetic pathname of the custom pack lab. The lab is served at `/` like the pack page, so the iframe reports
 * this one instead (see `useReportPageRoute`).
 */
export const CUSTOM_PACK_ROUTE = '/custom-pack';

/**
 * Map iframe pathnames → Design Constructor scene names. Each route the
 * preview iframe can sit on owns one scene; the sidebar filters its
 * component list down to entries with that scene (or no `scenes` field
 * at all — cross-cutting).
 */
const PATH_TO_SCENE: Record<string, Scene> = {
  '/': 'pack',
  '/just-pulled': 'just-pulled',
  '/my-packs': 'my-packs',
  [CUSTOM_PACK_ROUTE]: 'custom-pack',
  // Sleek's "What's inside" page — its tiles and sheet are pack-scene knobs.
  '/whats-inside': 'pack',
  // Reveal isn't a real router path — RevealScreen sends a
  // postMessage with this synthetic pathname so the Constructor
  // sidebar switches to the reveal-scene components while the
  // user is on the open-pack screen.
  '/reveal': 'reveal',
};

/**
 * Sub-paths that belong to a scene on one template only. Sleek's Ship Card
 * and delivery screens sit under `/my-packs/…`; the other templates keep the
 * show-everything sidebar they have always had on those paths.
 */
const TEMPLATE_PATH_PREFIXES: Partial<Record<ComponentTemplate, readonly [string, Scene][]>> = {
  sleek: [['/my-packs/', 'my-packs']],
};

export function sceneForPath(path: string, template: ComponentTemplate | undefined): Scene | null {
  const exact = PATH_TO_SCENE[path];
  if (exact) return exact;
  const prefixes = (template && TEMPLATE_PATH_PREFIXES[template]) ?? [];
  return prefixes.find(([prefix]) => path.startsWith(prefix))?.[1] ?? null;
}

function readScene(iframe: HTMLIFrameElement | null, template: ComponentTemplate | undefined): Scene | null {
  if (!iframe) return null;
  try {
    const doc = iframe.contentDocument;
    if (!doc) return null;
    return sceneForPath(doc.location.pathname, template);
  } catch {
    // Cross-origin (production preview-link) — we lose the ability to
    // read the path. Callers fall back to `null` and the sidebar treats
    // every scene as valid.
    return null;
  }
}

/**
 * Track the scene the embedded preview iframe is on. Updates on:
 *   • initial mount (after iframe load)
 *   • iframe navigation (`load` event re-fires on full reloads)
 *   • postMessage `design-constructor-route` from the child — sent by
 *     `setViewInURL` so SPA-style navigations (no full reload) still
 *     update the parent.
 *
 * Returns `null` until the first reading succeeds (or if cross-origin
 * blocks access). Consumers must treat `null` as "show every scene".
 */
export function useIframeScene(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  template?: ComponentTemplate,
): Scene | null {
  const [scene, setScene] = useState<Scene | null>(null);

  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return;
    const update = () => setScene(readScene(el, template));
    update();
    el.addEventListener('load', update);

    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: unknown; pathname?: unknown } | null;
      if (
        typeof data === 'object' &&
        data !== null &&
        data.type === 'design-constructor-route' &&
        typeof data.pathname === 'string'
      ) {
        setScene(sceneForPath(data.pathname, template));
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      el.removeEventListener('load', update);
      window.removeEventListener('message', onMessage);
    };
  }, [iframeRef, template]);

  return scene;
}
